import React, { FC } from 'react'
import classnames from 'classnames'
import { useHistory } from 'react-router-dom'
import { isMobile } from 'react-device-detect'

// Components
import { MenuTooltip } from 'components'

// Types
import { AnchorType, EventStages, GameFlowSteps, IUser, Routes } from 'types'

// Styles
import { Theme, makeStyles, createStyles } from '@material-ui/core'
import { StyledMenuList, StyledMenuListIcon, StyledMenuListItem, StyledOpenRightDrawer } from './Styled'

// Images
import demoTvIcon from 'assets/demoTvIcon.svg'
import mailIcon from 'assets/mailIcon.svg'
import sessionIcon from 'assets/breakout.svg'
import infoIcon from 'assets/infoIcon.svg'
import gradCapIcon from 'assets/gradCapIcon.svg'
import supportIcon from 'assets/supportIcon.svg'
import liveStreamIcon from 'assets/liveStream.svg'
import homeIcon from 'assets/homeIcon.svg'

import { createInteraction } from 'helpers'

interface MenuListProps {
  user?: IUser | null
  anchor: AnchorType
  drawerOpen: boolean
  toggleMenuDrawer: () => void
  toggleTutorial: () => void
  toggleProfileDrawer: () => void
  toggleIntroTutorial: () => void
  setGameState?: (state: GameFlowSteps) => void
  eventStage?: EventStages
}

export const MenuList: FC<MenuListProps> = ({
  drawerOpen,
  toggleMenuDrawer,
  user,
  setGameState,
  toggleIntroTutorial,
  toggleProfileDrawer,
  eventStage
}) => {
  const classes = useStyles()
  const history = useHistory()
  const handleMenuDrawerClose = () => {
    if (drawerOpen) {
      toggleMenuDrawer()
    }
  }

  return (
    <StyledOpenRightDrawer>
      {/* {drawerOpen ? (
        <StyledMenuListItem>
          <StyledMenuListIcon>
            <img src={homeIcon} alt='Home icon' width='25' />
          </StyledMenuListIcon>
          <div className={classnames([classes.menuText])}>Welcome</div>
        </StyledMenuListItem>
      ) : null} */}

      <StyledMenuList className={drawerOpen ? 'drawer-open' : 'drawer-close'}>
        <StyledMenuListItem
          onClick={() => {
            handleMenuDrawerClose()
            createInteraction('welcome', user?.email || '', 'navigation', 'clicked', eventStage)
            isMobile ? history.push(`/event`) : setGameState && setGameState(GameFlowSteps.Welcome)
          }}
          id='homeButton'
        >
          <MenuTooltip drawerOpen={drawerOpen} title='Home' placement='left'>
            <StyledMenuListIcon>
              <img src={homeIcon} alt='Home icon' width='25' />
            </StyledMenuListIcon>
          </MenuTooltip>
          <div
            className={classnames([classes.menuText], {
              [classes.menuTextDrawerClosed]: !drawerOpen
            })}
          >
            Welcome
          </div>
        </StyledMenuListItem>

        {eventStage === EventStages.POSTEVENT ? (
          <StyledMenuListItem
            onClick={() => {
              handleMenuDrawerClose()
              createInteraction('eventReplay', user?.email || '', 'navigation', 'clicked', eventStage)
              isMobile
                ? history.push('/event/event-replay/fullShow')
                : setGameState && setGameState(GameFlowSteps.EventReplay)
            }}
          >
            <MenuTooltip drawerOpen={drawerOpen} title='Event Replay' placement='left'>
              <StyledMenuListIcon>
                <img src={liveStreamIcon} alt='Event Replay icon' width='25' />
              </StyledMenuListIcon>
            </MenuTooltip>
            <div
              className={classnames([classes.menuText], {
                [classes.menuTextDrawerClosed]: !drawerOpen
              })}
            >
              Event Replay
            </div>
          </StyledMenuListItem>
        ) : (
          <StyledMenuListItem
            onClick={() => {
              handleMenuDrawerClose()
              createInteraction('livestream', user?.email || '', 'navigation', 'clicked', eventStage)
              history.push(`/${Routes.stream}`)
            }}
          >
            <MenuTooltip drawerOpen={drawerOpen} title='Livestream' placement='left'>
              <StyledMenuListIcon>
                <img src={liveStreamIcon} alt='Live Stream icon' width='25' />
              </StyledMenuListIcon>
            </MenuTooltip>
            <div
              className={classnames([classes.menuText], {
                [classes.menuTextDrawerClosed]: !drawerOpen
              })}
            >
              Livestream
            </div>
          </StyledMenuListItem>
        )}
        <StyledMenuListItem
          onClick={() => {
            handleMenuDrawerClose()
            createInteraction('sessions', user?.email || '', 'navigation', 'clicked', eventStage)
            isMobile ? history.push(`/event/${Routes.sessions}`) : setGameState && setGameState(GameFlowSteps.Sessions)
          }}
        >
          <MenuTooltip drawerOpen={drawerOpen} title='DOD 5G use cases' placement='left'>
            <StyledMenuListIcon>
              <img src={sessionIcon} alt='Breakout Session icon' width='16' />
            </StyledMenuListIcon>
          </MenuTooltip>
          <div
            className={classnames([classes.menuText], {
              [classes.menuTextDrawerClosed]: !drawerOpen
            })}
          >
            DOD 5G use cases
          </div>
        </StyledMenuListItem>
        <StyledMenuListItem
          onClick={() => {
            handleMenuDrawerClose()
            createInteraction('explore', user?.email || '', 'navigation', 'clicked', eventStage)
            isMobile ? history.push('/event/other-use-cases') : setGameState && setGameState(GameFlowSteps.Explore)
          }}
        >
          <MenuTooltip drawerOpen={drawerOpen} title='Other 5G use cases' placement='left'>
            <StyledMenuListIcon>
              <img src={demoTvIcon} alt='Demos icon' width='23' />
            </StyledMenuListIcon>
          </MenuTooltip>
          <div
            className={classnames([classes.menuText], {
              [classes.menuTextDrawerClosed]: !drawerOpen
            })}
          >
            Other 5G use cases
          </div>
        </StyledMenuListItem>
        <StyledMenuListItem
          onClick={() => {
            handleMenuDrawerClose()
            createInteraction('contactUs', user?.email || '', 'navigation', 'clicked', eventStage)
            isMobile ? history.push(`/event/${Routes.contact}`) : setGameState && setGameState(GameFlowSteps.Connect)
          }}
        >
          <MenuTooltip drawerOpen={drawerOpen} title='Contact us' placement='left'>
            <StyledMenuListIcon>
              <img src={mailIcon} alt='Mail icon' width='19' />
            </StyledMenuListIcon>
          </MenuTooltip>
          <div
            className={classnames([classes.menuText], {
              [classes.menuTextDrawerClosed]: !drawerOpen
            })}
          >
            Contact us
          </div>
        </StyledMenuListItem>
        <StyledMenuListItem
          onClick={() => {
            handleMenuDrawerClose()
            createInteraction('aboutTheEvent', user?.email || '', 'navigation', 'clicked', eventStage)
            isMobile ? history.push(`/event/${Routes.about}`) : window.postMessage('{"command":"about"}', '*')
          }}
        >
          <MenuTooltip drawerOpen={drawerOpen} title='About the event' placement='left'>
            <StyledMenuListIcon>
              <img src={infoIcon} alt='Info icon' width='19' />
            </StyledMenuListIcon>
          </MenuTooltip>
          <div
            className={classnames([classes.menuText], {
              [classes.menuTextDrawerClosed]: !drawerOpen
            })}
          >
            About the event
          </div>
        </StyledMenuListItem>
        <StyledMenuListItem
          onClick={() => {
            createInteraction('tutorial', user?.email || '', 'navigation', 'clicked', eventStage)
            toggleIntroTutorial()
          }}
        >
          <MenuTooltip drawerOpen={drawerOpen} title='Tutorial' placement='left'>
            <StyledMenuListIcon>
              <img src={gradCapIcon} alt='Grad cap icon' width='24' />
            </StyledMenuListIcon>
          </MenuTooltip>
          <div
            className={classnames([classes.menuText], {
              [classes.menuTextDrawerClosed]: !drawerOpen
            })}
          >
            Tutorial
          </div>
        </StyledMenuListItem>
        <StyledMenuListItem
          onClick={() => {
            createInteraction('support', user?.email || '', 'navigation', 'clicked', eventStage)
            if (isMobile) {
              history.push(`/event/${Routes.support}`)
              handleMenuDrawerClose()
            } else {
              window.postMessage('{"command":"support"}', '*')
            }
          }}
        >
          <MenuTooltip drawerOpen={drawerOpen} title='Technical support' placement='left'>
            <StyledMenuListIcon>
              <img src={supportIcon} alt='Support icon' width='19' />
            </StyledMenuListIcon>
          </MenuTooltip>
          <div
            className={classnames([classes.menuText], {
              [classes.menuTextDrawerClosed]: !drawerOpen
            })}
          >
            Technical support
          </div>
        </StyledMenuListItem>
      </StyledMenuList>
    </StyledOpenRightDrawer>
  )
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    menuTextDrawerClosed: {
      display: 'none'
    },
    menuText: {
      textOverflow: 'ellipsis'
    }
  })
)
